import React, { useState, useEffect } from 'react';
import { Search, DollarSign, Calendar, UserCheck, Users } from 'lucide-react';
import { masterSettingsAPI, studentsAPI } from '../../api/api';

const AssignOtherFees = () => {
  const [students, setStudents] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({
    student_id: '',
    fee_type_id: '',
    amount: '',
    due_date: ''
  });
  const [bulkFormData, setBulkFormData] = useState({
    fee_type_id: '',
    amount: '',
    due_date: '',
    assignment_type: 'all',
    course_id: '',
    semester_id: '',
    session_id: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [studentsRes, feeTypesRes, coursesRes, sessionsRes] = await Promise.all([
        studentsAPI.getStudents(),
        masterSettingsAPI.getAllOtherFeeTypes(),
        masterSettingsAPI.getCourses(),
        masterSettingsAPI.getSessions()
      ]);
      setStudents(studentsRes.data || []);
      setFeeTypes(feeTypesRes.data || []);
      setCourses(coursesRes.data || []);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to fetch data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignFee = (student) => {
    setSelectedStudent(student);
    setFormData({
      student_id: student.id,
      fee_type_id: '',
      amount: '',
      due_date: ''
    });
    setShowModal(true);
  };

  const handleFeeTypeChange = (feeTypeId) => {
    const selectedFeeType = feeTypes.find(f => f.id === feeTypeId);
    setFormData({
      ...formData,
      fee_type_id: feeTypeId,
      amount: selectedFeeType?.amount || ''
    });
  };

  const handleBulkFeeTypeChange = (feeTypeId) => {
    const selectedFeeType = feeTypes.find(f => f.id === feeTypeId);
    setBulkFormData({
      ...bulkFormData,
      fee_type_id: feeTypeId,
      amount: selectedFeeType?.amount || ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await masterSettingsAPI.assignOtherFee(formData);
      alert('Fee assigned successfully!');
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Error assigning fee:', error);
      alert(error.response?.data?.detail || 'Failed to assign fee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Filter students based on assignment type
      let targetStudents = [];
      
      switch(bulkFormData.assignment_type) {
        case 'all':
          targetStudents = students;
          break;
        case 'course':
          targetStudents = students.filter(s => s.course_id === bulkFormData.course_id);
          break;
        case 'semester':
          targetStudents = students.filter(s => s.semester_id === bulkFormData.semester_id);
          break;
        case 'session':
          targetStudents = students.filter(s => s.session_id === bulkFormData.session_id);
          break;
        default:
          targetStudents = students;
      }

      if (targetStudents.length === 0) {
        alert('No students found matching the criteria!');
        setLoading(false);
        return;
      }

      if (!window.confirm(`This will assign the fee to ${targetStudents.length} students. Continue?`)) {
        setLoading(false);
        return;
      }

      // Assign to each student
      for (const student of targetStudents) {
        await masterSettingsAPI.assignOtherFee({
          student_id: student.id,
          fee_type_id: bulkFormData.fee_type_id,
          amount: bulkFormData.amount,
          due_date: bulkFormData.due_date
        });
      }

      alert(`Fee assigned successfully to ${targetStudents.length} students!`);
      handleCloseBulkModal();
      fetchData();
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      alert(error.response?.data?.detail || 'Failed to assign fees. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setFormData({
      student_id: '',
      fee_type_id: '',
      amount: '',
      due_date: ''
    });
  };

  const handleCloseBulkModal = () => {
    setShowBulkModal(false);
    setBulkFormData({
      fee_type_id: '',
      amount: '',
      due_date: '',
      assignment_type: 'all',
      course_id: '',
      semester_id: '',
      session_id: ''
    });
  };

  const filteredStudents = students.filter(student =>
    student.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Assign Other Fees to Students</h1>
        <button
          onClick={() => setShowBulkModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
        >
          <Users size={18} className="mr-2" />
          Bulk Assignment
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
        <input
          type="text"
          placeholder="Search by student name or admission number..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Students Table */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Admission No
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Student Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Course
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredStudents.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-4 text-center text-gray-500">
                  {loading ? 'Loading students...' : 'No students found'}
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {student.admission_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {student.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.course_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleAssignFee(student)}
                      className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <DollarSign size={16} className="mr-1" />
                      Assign Fee
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Individual Assignment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Assign Other Fee
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Student: <span className="font-semibold">{selectedStudent?.full_name}</span> ({selectedStudent?.admission_number})
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fee Type *
                </label>
                <select
                  value={formData.fee_type_id}
                  onChange={(e) => handleFeeTypeChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Fee Type</option>
                  {feeTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Assigning...' : 'Assign Fee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Assignment Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl p-8 max-w-lg w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Bulk Assign Other Fee
            </h3>
            
            <form onSubmit={handleBulkSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Fee Type *
                </label>
                <select
                  value={bulkFormData.fee_type_id}
                  onChange={(e) => handleBulkFeeTypeChange(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Fee Type</option>
                  {feeTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  value={bulkFormData.amount}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, amount: e.target.value })}
                  required
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={bulkFormData.due_date}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, due_date: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign To *
                </label>
                <select
                  value={bulkFormData.assignment_type}
                  onChange={(e) => setBulkFormData({ ...bulkFormData, assignment_type: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Students</option>
                  <option value="course">Specific Course</option>
                  <option value="semester">Specific Semester</option>
                  <option value="session">Specific Session</option>
                </select>
              </div>

              {bulkFormData.assignment_type === 'course' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course *
                  </label>
                  <select
                    value={bulkFormData.course_id}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, course_id: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {bulkFormData.assignment_type === 'session' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Session *
                  </label>
                  <select
                    value={bulkFormData.session_id}
                    onChange={(e) => setBulkFormData({ ...bulkFormData, session_id: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Session</option>
                    {sessions.map((session) => (
                      <option key={session.id} value={session.id}>
                        {session.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseBulkModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                  disabled={loading}
                >
                  {loading ? 'Assigning...' : 'Assign to Students'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignOtherFees;
